<template>
  <div class="matching-page">
    <section
      ref="candidateArea"
      class="match-container"
      :style="candidateAreaStyle"
      :aria-busy="viewState === VIEW_STATE_LOADING"
    >
      <div
        class="matching-loading"
        role="status"
        aria-live="polite"
        v-if="viewState === VIEW_STATE_LOADING"
      >
        <span class="matching-loading__spinner" aria-hidden="true"></span>
        <span class="matching-loading__text">読み込み中です</span>
      </div>

      <div class="match-card-viewport">
        <article
          ref="card"
          class="match-card"
          :class="{
            'match-card--panning': isPanning,
            'match-card--button-exit': isButtonAnimating,
            'match-card--no-transition': suppressCardTransition,
          }"
          v-show="viewState === VIEW_STATE_CANDIDATE"
          :style="cardStyle"
        >
        <div v-if="currentOshiDetail.matchingComment" class="match-comment">
          <span class="match-comment__text" v-text="currentOshiDetail.matchingComment || ''"></span>
        </div>

        <div class="match-profile">
          <img
            class="match-profile__icon"
            :src="currentUser.iconImagePath"
            :alt="`${currentUser.nickname}のアイコン画像`"
          >
          <span class="match-profile__nickname" v-text="currentUser.nickname"></span>
        </div>

        <p class="match-profile-description">
          <template v-for="(line, index) in profileLines" :key="index">
            <br v-if="index > 0">
            <span v-text="line"></span>
          </template>
        </p>

        <div class="match-banner">
          <img
            class="match-banner__image"
            :src="currentBannerImage"
            :alt="`${currentUser.nickname}の紹介画像`"
          >
        </div>

        <div class="match-tags" aria-label="推しタグ">
          <span
            v-for="(tag, index) in displayTags"
            :key="`${tag.name}-${index}`"
            class="match-tag"
            :class="tagClass(tag)"
            v-text="tag.name"
          ></span>
        </div>

        <div class="match-actions">
          <button
            type="button"
            class="match-action match-action--dislike"
            :disabled="isControlDisabled || previewMode"
            aria-label="いまいちにする"
            title="いまいちにする"
            @click="handleDecisionButton(false)"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="match-action match-action--youtube"
            :disabled="isYoutubeButtonDisabled"
            aria-label="YouTube動画を表示"
            title="YouTube動画を表示"
            @click="openYoutubeModal"
          >
            <i class="fab fa-youtube" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="match-action match-action--like"
            :disabled="isControlDisabled || previewMode"
            aria-label="好きにする"
            title="好きにする"
            @click="handleDecisionButton(true)"
          >
            <i class="fas fa-heart" aria-hidden="true"></i>
          </button>
        </div>
        </article>
        <div
          class="no-users-message"
          v-show="viewState === VIEW_STATE_EMPTY"
        >
          ただいまマッチングできるユーザーがいません。お手数ですが時間をおいて再度お試しください。
        </div>
      </div>

      <div
        class="like-label"
        :class="{animate: shouldAnimateLiked}"
        v-show="viewState === VIEW_STATE_CANDIDATE && shouldShowLiked"
      >
        <span class="label-text" :class="{enlarged: shouldAnimateLiked}">好き</span>
        <div class="emoji-animation" :class="{start: shouldAnimateLiked}">
          &#x2764;&#xFE0F; &#x2764;&#xFE0F; &#x2764;&#xFE0F;
        </div>
      </div>

      <div
        class="dislike-label"
        :class="{animate: shouldAnimateDisliked}"
        v-show="viewState === VIEW_STATE_CANDIDATE && shouldShowDisliked"
      >
        <span class="label-text" :class="{enlarged: shouldAnimateDisliked}">いまいち</span>
        <div class="emoji-animation" :class="{start: shouldAnimateDisliked}">
          &#x1F4A6; &#x1F4A6; &#x1F4A6;
        </div>
      </div>
    </section>

    <Teleport to="body">
      <div
        v-if="isGuestInitialModalOpen"
        ref="guestInitialModal"
        class="modal fade show matching-guide-modal matching-guide-modal--initial"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-label="未ログインのご案内"
        @click.self="closeGuestInitialModal"
      >
        <div class="modal-dialog matching-guide-modal__dialog" role="document">
          <div class="modal-content matching-guide-modal__content">
            <div class="matching-guide-modal__warning" aria-hidden="true">
              <i class="fas fa-triangle-exclamation"></i>
            </div>
            <button
              type="button"
              class="matching-guide-modal__close"
              aria-label="閉じる"
              @click="closeGuestInitialModal"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <div class="modal-body matching-guide-modal__body">
              <p>未ログインのためマッチングできません<br>どんな推しがいるかのみ確認できます。<br><br>アカウントをお持ちでない方は会員登録の上お試しください</p>
              <button type="button" class="matching-guide-modal__button" @click="closeGuestInitialModal">閉じる</button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isGuestEndModalOpen"
        ref="guestEndModal"
        class="modal fade show matching-guide-modal matching-guide-modal--end"
        tabindex="-1"
        role="alertdialog"
        aria-modal="true"
        aria-label="ログインのご案内"
      >
        <div class="modal-dialog matching-guide-modal__dialog" role="document">
          <div class="modal-content matching-guide-modal__content">
            <div class="matching-guide-modal__warning" aria-hidden="true">
              <i class="fas fa-triangle-exclamation"></i>
            </div>
            <div class="modal-body matching-guide-modal__body">
              <p>未ログインのためマッチングできません<br>どんな推しがいるかのみ確認できます。<br><br>アカウントをお持ちでない方は会員登録の上お試しください</p>
              <a class="matching-guide-modal__button" :href="loginUrl">ログインはこちら</a>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isDetailModalOpen"
        ref="detailModal"
        class="modal fade show matching-detail-modal"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-label="詳細"
        @click.self="closeDetailModal"
      >
        <div class="modal-dialog matching-detail-modal__dialog" role="document">
          <div class="modal-content matching-detail-modal__content">
            <button
              type="button"
              class="matching-detail-modal__close"
              aria-label="閉じる"
              @click="closeDetailModal"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <div class="modal-body matching-detail-modal__body">
              <iframe
                class="matching-detail-modal__iframe"
                :src="currentOshiDetailUrl"
                :title="`${currentUser.nickname}の推し詳細`"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isYoutubeModalOpen"
        ref="youtubeModal"
        class="modal fade show matching-youtube-modal"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-label="YouTube動画"
        @click.self="closeYoutubeModal"
      >
        <div class="modal-dialog matching-youtube-modal__dialog" role="document">
          <div class="modal-content matching-youtube-modal__content">
            <button
              type="button"
              class="matching-youtube-modal__close"
              aria-label="閉じる"
              @click="closeYoutubeModal"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <div class="modal-body matching-youtube-modal__body">
              <iframe
                v-if="activeYoutubeEmbedLink"
                class="matching-youtube-modal__iframe"
                :src="activeYoutubeEmbedLink"
                title="推しユーザーのYouTube動画"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isErrorModalOpen"
        ref="errorModal"
        class="modal fade show matching-error-modal"
        tabindex="-1"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="matching-error-modal-title"
      >
        <div class="modal-dialog matching-guide-modal__dialog" role="document">
          <div class="modal-content matching-guide-modal__content">
            <div class="matching-guide-modal__warning" aria-hidden="true">
              <i class="fas fa-triangle-exclamation"></i>
            </div>
            <div class="modal-body matching-guide-modal__body">
              <h2 id="matching-error-modal-title" class="matching-error-modal__title">エラー</h2>
              <p>通信に失敗しました<br>時間をおいて再度お試しください<span v-if="isDecisionError"><br><br>マッチング相手を再度表示する場合は画面リロードをお願いいたします</span></p>
              <button type="button" class="matching-guide-modal__button" @click="closeErrorModal">閉じる</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    <AppNotification
      :notification="notification.current.value"
      :visible="notification.visible.value"
      @close="notification.close"
      @after-leave="notification.afterLeave"
      @activate="activateNotification"
    />
  </div>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref} from 'vue';
import {useSwipeGesture} from '../composables/useSwipeGesture';
import {internalNotificationPath, useAppNotification} from '../composables/useAppNotification';
import {fetchCandidates, submitDecision} from '../services/matchingApi';
import AppNotification from './common/AppNotification.vue';

const props = defineProps({
  appEnvironment: {
    type: String,
    default: 'prod',
  },
  isAuthenticated: {
    type: Boolean,
    default: true,
  },
  loginUrl: {
    type: String,
    default: '/login',
  },
  oshiDetailUrlTemplate: {
    type: String,
    default: '/oshi/detail/%public_customer_id%',
  },
  previewMode: {
    type: Boolean,
    default: false,
  },
  previewCandidate: {
    type: Object,
    default: null,
  },
});

const VIEW_STATE_LOADING = 'loading';
const VIEW_STATE_CANDIDATE = 'candidate';
const VIEW_STATE_EMPTY = 'empty';
const EMPTY_LOADING_DURATION = 2000;
const INITIAL_LOADING_DURATION = 1250;
const BUTTON_EXIT_DURATION = 600;
const SP_LAYOUT_MAX_WIDTH = 767;
const SP_SWIPE_MAX_WIDTH = 480;
const EMPTY_OSHI_DETAIL = Object.freeze({
  profile: '',
  profileImagePath: '',
  miniBannerImagePath: '',
  previewImageRequiresXhr: false,
  chatEnabledFlg: true,
  freeChatEnabledFlg: false,
  matchingComment: null,
  matchingYoutubeEmbedLink: null,
});
const EMPTY_CANDIDATE = Object.freeze({
  publicCustomerId: '',
  nickname: '',
  iconImagePath: '',
  oshiDetail: EMPTY_OSHI_DETAIL,
  oshiTags: [],
});

const logError = (...messages) => {
  if (props.appEnvironment !== 'prod') {
    console.error(...messages);
  }
};

const card = ref(null);
const candidateArea = ref(null);
const guestInitialModal = ref(null);
const guestEndModal = ref(null);
const detailModal = ref(null);
const youtubeModal = ref(null);
const errorModal = ref(null);
const candidates = ref([]);
const currentCandidateIndex = ref(0);
const viewState = ref(VIEW_STATE_LOADING);
const commitThreshold = ref(200);
const candidateAreaHeight = ref(null);
const isButtonAnimating = ref(false);
const buttonDecision = ref(null);
const buttonExitX = ref(0);
const suppressCardTransition = ref(false);
const isYoutubeModalOpen = ref(false);
const isGuestInitialModalOpen = ref(!props.isAuthenticated);
const isGuestEndModalOpen = ref(false);
const isDetailModalOpen = ref(false);
const isErrorModalOpen = ref(false);
const isDecisionError = ref(false);
const activeYoutubeEmbedLink = ref('');
const notification = useAppNotification();
let emptyTimer = null;
let initialLoadingTimer = null;
let resolveInitialLoading = null;
let buttonExitTimer = null;
let isActive = false;
let modalTriggerElement = null;

const currentUser = computed(() => candidates.value[currentCandidateIndex.value] || EMPTY_CANDIDATE);
const currentOshiDetail = computed(() => currentUser.value.oshiDetail || EMPTY_OSHI_DETAIL);
const profileLines = computed(() => String(currentOshiDetail.value.profile || '').split(/\r\n|\r|\n/));
const previewBannerObjectUrl = ref(null);
const currentBannerImage = computed(() => (
  currentOshiDetail.value.previewImageRequiresXhr
    ? (previewBannerObjectUrl.value || currentOshiDetail.value.profileImagePath)
    : (currentOshiDetail.value.miniBannerImagePath || currentOshiDetail.value.profileImagePath)
));
const currentOshiDetailUrl = computed(() => props.oshiDetailUrlTemplate.replace(
  '%public_customer_id%',
  encodeURIComponent(currentUser.value.publicCustomerId),
));
const candidateAreaStyle = computed(() => (
  candidateAreaHeight.value === null
    ? {}
    : {height: `${candidateAreaHeight.value}px`}
));
const isAnyModalOpen = computed(() => (
  isYoutubeModalOpen.value
  || isGuestInitialModalOpen.value
  || isGuestEndModalOpen.value
  || isDetailModalOpen.value
  || isErrorModalOpen.value
));
const isControlDisabled = computed(() => (
  viewState.value !== VIEW_STATE_CANDIDATE || isButtonAnimating.value || isAnyModalOpen.value
));
const isSwipeDisabled = computed(() => (
  isControlDisabled.value || props.previewMode
));
const isYoutubeButtonDisabled = computed(() => (
  isControlDisabled.value || !currentOshiDetail.value.matchingYoutubeEmbedLink
));
const shouldShowLiked = computed(() => isLiked.value || buttonDecision.value === true);
const shouldShowDisliked = computed(() => isDisliked.value || buttonDecision.value === false);
const shouldAnimateLiked = computed(() => isAnimateLiked.value || buttonDecision.value === true);
const shouldAnimateDisliked = computed(() => isAnimateDisliked.value || buttonDecision.value === false);

const displayTags = computed(() => currentUser.value.oshiTags || []);

const tagClass = (tag) => {
  if (tag.statusType === 'chat-disabled') {
    return 'match-tag--chat-disabled';
  }
  if (tag.statusType === 'free-chat') {
    return 'match-tag--free-chat';
  }
  if (tag.parentOshiTagType <= 2) {
    return 'match-tag--group-one';
  }
  if (tag.parentOshiTagType <= 4) {
    return 'match-tag--group-two';
  }
  if (tag.parentOshiTagType <= 6) {
    return 'match-tag--group-three';
  }
  if (tag.parentOshiTagType <= 8) {
    return 'match-tag--group-four';
  }

  return 'match-tag--group-five';
};

const cardStyle = computed(() => ({
  transform: `translateX(${isButtonAnimating.value ? buttonExitX.value : translateX.value}px)`,
}));

const clearEmptyTimer = () => {
  if (emptyTimer !== null) {
    window.clearTimeout(emptyTimer);
    emptyTimer = null;
  }
};

const clearButtonExitTimer = () => {
  if (buttonExitTimer !== null) {
    window.clearTimeout(buttonExitTimer);
    buttonExitTimer = null;
  }
};

const clearInitialLoadingTimer = () => {
  if (initialLoadingTimer !== null) {
    window.clearTimeout(initialLoadingTimer);
    initialLoadingTimer = null;
  }
  if (resolveInitialLoading !== null) {
    const resolve = resolveInitialLoading;
    resolveInitialLoading = null;
    resolve();
  }
};

const waitForInitialLoading = () => new Promise((resolve) => {
  resolveInitialLoading = resolve;
  initialLoadingTimer = window.setTimeout(() => {
    initialLoadingTimer = null;
    resolveInitialLoading = null;
    resolve();
  }, INITIAL_LOADING_DURATION);
});

const showEmptyAfterLoading = () => {
  clearEmptyTimer();
  viewState.value = VIEW_STATE_LOADING;
  emptyTimer = window.setTimeout(() => {
    emptyTimer = null;
    if (isActive) {
      viewState.value = VIEW_STATE_EMPTY;
    }
  }, EMPTY_LOADING_DURATION);
};

const openErrorModal = (decisionError = false) => {
  rememberModalTrigger();
  isDecisionError.value = decisionError;
  isErrorModalOpen.value = true;
  focusModal(errorModal);
};

const closeErrorModal = () => {
  isErrorModalOpen.value = false;
  isDecisionError.value = false;
  restoreModalTrigger();
};

const loadNextCandidates = async () => {
  viewState.value = VIEW_STATE_LOADING;
  try {
    const responseCandidates = await fetchCandidates();
    if (!isActive) {
      return;
    }
    if (responseCandidates.length === 0) {
      showEmptyAfterLoading();
      return;
    }
    candidates.value = responseCandidates;
    currentCandidateIndex.value = 0;
    viewState.value = VIEW_STATE_CANDIDATE;
  } catch (error) {
    logError('候補の取得に失敗しました。', error);
    if (isActive) {
      viewState.value = VIEW_STATE_EMPTY;
      openErrorModal();
    }
  }
};

const showNextCandidate = async (decisionResult = Promise.resolve()) => {
  if (currentCandidateIndex.value < candidates.value.length - 1) {
    currentCandidateIndex.value += 1;
    return;
  }

  if (!props.isAuthenticated) {
    viewState.value = VIEW_STATE_EMPTY;
    isGuestEndModalOpen.value = true;
    focusModal(guestEndModal);
    return;
  }

  viewState.value = VIEW_STATE_LOADING;
  await decisionResult;
  if (isActive) {
    await loadNextCandidates();
  }
};

const commitDecision = (liked, deferCandidateChange = false) => {
  if (props.previewMode) {
    return Promise.resolve();
  }
  let decisionResult = Promise.resolve();
  if (props.isAuthenticated) {
    decisionResult = submitDecision(currentUser.value.publicCustomerId, liked).then((result) => {
      if (result !== null && typeof result.message === 'string' && typeof result.url === 'string') {
        notification.show({
          type: 'info',
          message: result.message,
          url: result.url,
          iconUrl: result.icon_url,
        });
      }
      return result;
    }).catch((error) => {
      logError('Like・Dislikeの送信に失敗しました。', error);
      if (isActive) {
        openErrorModal(true);
      }
    });
  }

  if (!deferCandidateChange) {
    showNextCandidate(decisionResult);
  }

  return decisionResult;
};

const activateNotification = (item) => {
  const path = internalNotificationPath(item.url);
  if (path === null) return;
  notification.close();
  window.location.assign(path);
};

const handleSwipeCommit = (liked) => {
  if (props.previewMode || isControlDisabled.value) {
    return;
  }

  commitDecision(liked);
};

const {
  translateX,
  isLiked,
  isDisliked,
  isAnimateLiked,
  isAnimateDisliked,
  isPanning,
  reset: resetSwipe,
} = useSwipeGesture({
  target: card,
  threshold: commitThreshold,
  disabled: isSwipeDisabled,
  onCommit: handleSwipeCommit,
});

const handleDecisionButton = (liked) => {
  if (props.previewMode || isControlDisabled.value) {
    return;
  }

  resetSwipe();
  buttonDecision.value = liked;
  isButtonAnimating.value = true;
  const areaWidth = candidateArea.value?.clientWidth || window.innerWidth;
  buttonExitX.value = (areaWidth + 200) * (liked ? 1 : -1);
  const decisionResult = commitDecision(liked, true);

  buttonExitTimer = window.setTimeout(async () => {
    buttonExitTimer = null;
    if (!isActive) {
      return;
    }

    suppressCardTransition.value = true;
    showNextCandidate(decisionResult);
    buttonExitX.value = 0;
    isButtonAnimating.value = false;
    buttonDecision.value = null;
    await nextTick();
    suppressCardTransition.value = false;
  }, BUTTON_EXIT_DURATION);
};

const rememberModalTrigger = (triggerElement = null) => {
  modalTriggerElement = triggerElement instanceof HTMLElement
    ? triggerElement
    : (document.activeElement instanceof HTMLElement ? document.activeElement : null);
};

const focusModal = async (modalRef) => {
  await nextTick();
  modalRef.value?.focus({preventScroll: true});
};

const restoreModalTrigger = async () => {
  const target = modalTriggerElement;
  modalTriggerElement = null;
  await nextTick();
  if (target?.isConnected) {
    target.focus({preventScroll: true});
  }
};

const openYoutubeModal = (event) => {
  if (isYoutubeButtonDisabled.value) {
    return;
  }

  rememberModalTrigger(event.currentTarget);
  activeYoutubeEmbedLink.value = currentOshiDetail.value.matchingYoutubeEmbedLink;
  isYoutubeModalOpen.value = true;
  focusModal(youtubeModal);
};

const closeYoutubeModal = (shouldRestoreFocus = true) => {
  isYoutubeModalOpen.value = false;
  activeYoutubeEmbedLink.value = '';
  if (shouldRestoreFocus) {
    restoreModalTrigger();
  }
};

const closeGuestInitialModal = () => {
  isGuestInitialModalOpen.value = false;
  restoreModalTrigger();
};

const openDetailModal = (event) => {
  if (
    props.previewMode
    ||
    isAnyModalOpen.value
    || viewState.value !== VIEW_STATE_CANDIDATE
    || currentUser.value.publicCustomerId === ''
  ) {
    return;
  }
  rememberModalTrigger(event.detail?.trigger);
  isDetailModalOpen.value = true;
  focusModal(detailModal);
};

const closeDetailModal = () => {
  isDetailModalOpen.value = false;
  restoreModalTrigger();
};

const getActiveModal = () => {
  if (isErrorModalOpen.value) {
    return errorModal.value;
  }
  if (isYoutubeModalOpen.value) {
    return youtubeModal.value;
  }
  if (isDetailModalOpen.value) {
    return detailModal.value;
  }
  if (isGuestEndModalOpen.value) {
    return guestEndModal.value;
  }
  if (isGuestInitialModalOpen.value) {
    return guestInitialModal.value;
  }

  return null;
};

const trapModalFocus = (event) => {
  const modal = getActiveModal();
  if (modal === null) {
    return;
  }

  const focusableElements = [...modal.querySelectorAll(
    'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
  )];
  if (focusableElements.length === 0) {
    event.preventDefault();
    modal.focus({preventScroll: true});
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === modal)) {
    event.preventDefault();
    lastElement.focus({preventScroll: true});
  } else if (!event.shiftKey && (document.activeElement === lastElement || document.activeElement === modal)) {
    event.preventDefault();
    firstElement.focus({preventScroll: true});
  }
};

const handleKeydown = (event) => {
  if (event.key === 'Tab') {
    trapModalFocus(event);
    return;
  }
  if (event.key !== 'Escape') {
    return;
  }
  if (isYoutubeModalOpen.value) {
    closeYoutubeModal();
  } else if (isErrorModalOpen.value) {
    closeErrorModal();
  } else if (isDetailModalOpen.value) {
    closeDetailModal();
  } else if (isGuestInitialModalOpen.value) {
    closeGuestInitialModal();
  }
};

const updateResponsiveState = () => {
  commitThreshold.value = window.innerWidth <= SP_SWIPE_MAX_WIDTH ? 100 : 200;

  if (window.innerWidth > SP_LAYOUT_MAX_WIDTH || candidateArea.value === null) {
    candidateAreaHeight.value = null;
    return;
  }

  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const areaTop = candidateArea.value.getBoundingClientRect().top;
  candidateAreaHeight.value = Math.max(0, Math.floor(viewportHeight - areaTop));
};

const loadCandidates = async () => {
  viewState.value = VIEW_STATE_LOADING;

  if (props.previewMode) {
    candidates.value = props.previewCandidate === null ? [] : [props.previewCandidate];
    currentCandidateIndex.value = 0;
    const previewImageUrl = props.previewCandidate?.oshiDetail?.previewImageRequiresXhr
      ? props.previewCandidate.oshiDetail.miniBannerImagePath
      : null;
    if (previewImageUrl) {
      try {
        const response = await fetch(previewImageUrl, {
          credentials: 'same-origin',
          headers: {'X-Requested-With': 'XMLHttpRequest'},
        });
        if (!response.ok) throw new Error('Preview image could not be loaded');
        previewBannerObjectUrl.value = URL.createObjectURL(await response.blob());
      } catch (error) {
        logError('プレビュー画像の取得に失敗しました。', error);
      }
    }
    viewState.value = candidates.value.length === 0 ? VIEW_STATE_EMPTY : VIEW_STATE_CANDIDATE;
    return;
  }

  const candidateResult = fetchCandidates()
    .then((responseCandidates) => ({responseCandidates, error: null}))
    .catch((error) => ({responseCandidates: [], error}));
  const [{responseCandidates, error}] = await Promise.all([
    candidateResult,
    waitForInitialLoading(),
  ]);

  if (!isActive) {
    return;
  }
  if (error !== null) {
    logError('候補の取得に失敗しました。', error);
    viewState.value = VIEW_STATE_EMPTY;
    openErrorModal();
    return;
  }
  if (responseCandidates.length === 0) {
    viewState.value = VIEW_STATE_EMPTY;
    return;
  }

  candidates.value = responseCandidates;
  currentCandidateIndex.value = 0;
  viewState.value = VIEW_STATE_CANDIDATE;
};

onMounted(() => {
  isActive = true;
  modalTriggerElement = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
  window.addEventListener('resize', updateResponsiveState);
  window.visualViewport?.addEventListener('resize', updateResponsiveState);
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('matching:open-detail', openDetailModal);
  nextTick(updateResponsiveState);
  if (isGuestInitialModalOpen.value) {
    focusModal(guestInitialModal);
  }
  loadCandidates();
});

onBeforeUnmount(() => {
  isActive = false;
  if (previewBannerObjectUrl.value !== null) URL.revokeObjectURL(previewBannerObjectUrl.value);
  clearEmptyTimer();
  clearInitialLoadingTimer();
  clearButtonExitTimer();
  closeYoutubeModal(false);
  window.removeEventListener('resize', updateResponsiveState);
  window.visualViewport?.removeEventListener('resize', updateResponsiveState);
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('matching:open-detail', openDetailModal);
});
</script>

<style>
.matching-ribbon {
  --matching-ribbon-pull-x: 50%;
  --matching-ribbon-pull-color: #7668a9;
  --matching-ribbon-pull-distance: 0px;

  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  height: 40px;
  padding: 0;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  background: linear-gradient(110deg, #a86f96 0%, #7668a9 48%, #a86f96 100%);
  border: 0;
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.14), 0 4px 14px rgba(72, 65, 101, 0.2);
  transition: filter 0.2s ease, box-shadow 0.2s ease;
}

.matching-ribbon::after {
  position: absolute;
  top: calc(100% - 1px);
  left: var(--matching-ribbon-pull-x);
  width: 220px;
  height: var(--matching-ribbon-pull-distance);
  pointer-events: none;
  content: '';
  background: var(--matching-ribbon-pull-color);
  clip-path: ellipse(50% 100% at 50% 0%);
  transform: translateX(-50%);
}

.matching-ribbon--releasing::after {
  transition: height 0.18s cubic-bezier(0.22, 1, 0.36, 1);
}

.matching-ribbon:hover {
  filter: brightness(1.06);
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.2), 0 5px 18px rgba(72, 65, 101, 0.28);
}

.matching-ribbon .fa-chevron-down {
  font-size: 12px;
  animation: matching-ribbon-chevron 1.8s ease-in-out infinite;
}

.matching-ribbon:focus-visible {
  outline: 3px solid #fff;
  outline-offset: -3px;
}

.matching_page .ec-layoutRole__main.ec-layoutRole__main--subpage {
  padding-top: 0;
}
</style>

<style scoped>
.matching-page {
  width: 100%;
}

.match-container {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 600px;
  margin: 0 auto 40px;
  overflow: hidden;
  text-align: center;
}

.match-card {
  position: relative;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  max-width: 440px;
  height: 100%;
  max-height: 600px;
  padding: 18px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(52, 52, 64, 0.12);
  transition: transform 0.3s ease-out;
}

.match-card-viewport {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.match-card--panning,
.match-card--no-transition {
  transition: none;
}

.match-card--button-exit {
  transition-duration: 600ms;
  transition-timing-function: ease-in;
}

.match-comment {
  position: relative;
  flex: 0 0 38px;
  box-sizing: border-box;
  width: 100%;
  padding: 9px 14px;
  color: #525263;
  text-align: left;
  background: #f2f2f5;
  border-radius: 13px;
}

.match-comment::after {
  position: absolute;
  bottom: -8px;
  left: 15px;
  width: 0;
  height: 0;
  content: '';
  border-top: 10px solid #f2f2f5;
  border-right: 9px solid transparent;
  border-left: 2px solid transparent;
  transform: rotate(18deg);
}

.match-comment__text,
.match-profile__nickname {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.match-profile {
  display: flex;
  flex: 0 0 50px;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.match-profile__icon {
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 50%;
}

.match-profile__nickname {
  min-width: 0;
  color: #363642;
  font-size: 19px;
  font-weight: 700;
  text-align: left;
}

.match-profile-description {
  display: -webkit-box;
  flex: 0 0 66px;
  height: 66px;
  margin: 0;
  overflow: hidden;
  color: #525263;
  font-size: 14px;
  line-height: 22px;
  text-align: left;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.match-banner {
  flex: 1 1 auto;
  width: 100%;
  min-height: 100px;
  max-height: 220px;
  overflow: hidden;
  background: #eee;
  border-radius: 12px;
  aspect-ratio: 355 / 135;
}

.match-banner__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.match-tags {
  display: flex;
  flex: 0 0 66px;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 6px;
  box-sizing: border-box;
  height: 66px;
  padding: 4px 0;
  overflow: hidden;
}

.match-tag {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 11px;
  color: #4c4c58;
  font-size: 13px;
  line-height: 20px;
  border-radius: 999px;
}

.match-tag--group-one {
  background: #fde8ee;
}

.match-tag--group-two {
  background: #e5f2fc;
}

.match-tag--group-three {
  background: #eee8fb;
}

.match-tag--group-four {
  background: #fff2cc;
}

.match-tag--group-five {
  background: #ddf4f1;
}

.match-tag--chat-disabled {
  color: #666;
  background: #ebebeb;
}

.match-tag--free-chat {
  color: #27603e;
  background: #e2f5e9;
}

.match-actions {
  display: flex;
  flex: 0 0 62px;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.match-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  padding: 0;
  font-size: 25px;
  cursor: pointer;
  background: #fff;
  border: 2px solid currentColor;
  border-radius: 50%;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.match-action:hover:not(:disabled),
.match-action:focus-visible:not(:disabled) {
  transform: translateY(-2px) scale(1.04);
}

.match-action:focus-visible {
  outline: 3px solid rgba(82, 82, 99, 0.25);
  outline-offset: 3px;
}

.match-action:disabled {
  cursor: not-allowed;
  opacity: 0.32;
}

.match-action--dislike {
  color: #627d98;
}

.match-action--like {
  color: #e8587a;
}

.match-action--youtube {
  color: #e62117;
}

.matching-loading {
  position: absolute;
  z-index: 60;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
}

.matching-loading__spinner {
  display: block;
  width: 48px;
  height: 48px;
  border: 5px solid #d7d7d7;
  border-top-color: #525263;
  border-radius: 50%;
  animation: matching-loading-spin 0.8s linear infinite;
}

.matching-loading__text {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.like-label,
.dislike-label {
  position: absolute;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 100%;
  overflow: hidden;
  font-size: 30px;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.like-label {
  right: 0;
  color: #8b0000;
  background: linear-gradient(to left, #ffcccb, transparent);
}

.dislike-label {
  left: 0;
  color: #00008b;
  background: linear-gradient(to right, #add8e6, transparent);
}

.label-text {
  position: relative;
  z-index: 100;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.9), 0 0 7px rgba(255, 255, 255, 0.65);
  transition: transform 0.3s ease, font-size 0.3s ease;
}

.label-text.enlarged {
  font-weight: 700;
  transform: scale(1.3);
}

.emoji-animation {
  position: absolute;
  bottom: calc(50% + 15px);
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  pointer-events: none;
  opacity: 0;
  transform: translateY(20px);
}

.emoji-animation.start {
  opacity: 1;
  animation: float-up 0.75s infinite linear;
}

.matching-youtube-modal {
  display: block;
  padding: 16px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.65);
}

.matching-guide-modal,
.matching-detail-modal,
.matching-error-modal {
  display: block;
  padding: 16px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.55);
}

.matching-guide-modal:focus,
.matching-detail-modal:focus,
.matching-youtube-modal:focus,
.matching-error-modal:focus {
  outline: none;
}

.matching-guide-modal__dialog,
.matching-detail-modal__dialog {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 560px;
  min-height: calc(100% - 3.5rem);
  margin: 1.75rem auto;
}

.matching-guide-modal__content,
.matching-detail-modal__content {
  position: relative;
  background: #fff;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.matching-guide-modal__warning {
  position: absolute;
  top: 18px;
  left: 20px;
  color: #888;
  font-size: 25px;
}

.matching-guide-modal__close,
.matching-detail-modal__close {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 1;
  padding: 0;
  color: #777;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.matching-guide-modal__close:focus-visible,
.matching-detail-modal__close:focus-visible {
  outline: 3px solid #888;
  outline-offset: 3px;
}

.matching-guide-modal__body {
  padding: 64px 28px 28px;
  color: #454550;
  font-size: 15px;
  line-height: 1.8;
  text-align: center;
}

.matching-guide-modal__body p {
  margin: 0 0 24px;
}

.matching-error-modal__title {
  margin: 0 0 14px;
  color: #555;
  font-size: 22px;
  font-weight: 700;
}

.matching-guide-modal__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  min-height: 44px;
  padding: 8px 20px;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  background: #666;
  border: 0;
  border-radius: 6px;
}

.matching-guide-modal__button:hover,
.matching-guide-modal__button:focus-visible {
  color: #fff;
  text-decoration: none;
  background: #555;
}

.matching-detail-modal__body {
  width: 100%;
  height: 100%;
  padding: 0;
}

.matching-detail-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  overflow: hidden;
}

.matching-detail-modal__dialog {
  width: 100%;
  max-width: 430px;
  height: min(700px, calc(100vh - 128px));
  height: min(700px, calc(100dvh - 128px));
  min-height: 0;
  margin: 0;
}

.matching-detail-modal__content {
  display: grid;
  grid-template-rows: 40px minmax(0, 1fr);
  width: 100%;
  height: 100%;
  overflow: visible;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.matching-detail-modal__close {
  position: static;
  align-self: center;
  justify-self: end;
  width: 36px;
  height: 36px;
  color: #fff;
  font-size: 36px;
}

.matching-detail-modal__iframe {
  display: block;
  width: 100%;
  height: 100%;
  background: #fff;
  border: 0;
}

.matching-youtube-modal__dialog {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 900px;
  min-height: calc(100% - 3.5rem);
  margin: 1.75rem auto;
}

.matching-youtube-modal__content {
  position: relative;
  overflow: visible;
  background: #000;
  border: 0;
}

.matching-youtube-modal__close {
  position: absolute;
  top: -42px;
  right: 0;
  z-index: 1;
  padding: 0;
  color: #fff;
  font-size: 34px;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.matching-youtube-modal__close:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 3px;
}

.matching-youtube-modal__body {
  padding: 0;
  aspect-ratio: 16 / 9;
}

.matching-youtube-modal__iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

@keyframes matching-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes matching-ribbon-chevron {
  0%,
  100% {
    transform: translateY(-1px);
  }

  50% {
    transform: translateY(3px);
  }
}

@keyframes float-up {
  from {
    transform: translateY(30px);
  }

  to {
    transform: translateY(-30px);
  }
}

@media screen and (max-width: 767px) {
  .match-container {
    margin-bottom: 24px;
    overflow: visible;
  }

  .match-profile {
    flex-basis: 60px;
  }

  .match-profile__icon {
    flex-basis: 60px;
    width: 60px;
    height: 60px;
  }

  .match-banner {
    aspect-ratio: 355 / 150;
  }

  .like-label {
    right: calc((100% - 100vw) / 2);
  }

  .dislike-label {
    left: calc((100% - 100vw) / 2);
  }
}

@media screen and (max-width: 480px) {
  .match-card {
    gap: 8px;
    max-width: 100%;
    padding: 12px;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  }

  .match-comment {
    flex-basis: 34px;
    padding: 7px 11px;
    font-size: 13px;
  }

  .match-profile {
    gap: 9px;
  }

  .match-profile__nickname {
    font-size: 17px;
  }

  .match-banner {
    min-height: 80px;
  }

  .match-actions {
    flex-basis: 54px;
    gap: 20px;
  }

  .match-action {
    width: 50px;
    height: 50px;
    font-size: 22px;
  }

  .like-label,
  .dislike-label {
    width: 120px;
    font-size: 19px;
  }

  .matching-youtube-modal {
    padding: 10px;
  }

  .matching-youtube-modal__dialog {
    min-height: calc(100% - 2rem);
    margin: 1rem auto;
  }

  .matching-guide-modal,
  .matching-error-modal {
    padding: 10px;
  }

  .matching-guide-modal__dialog {
    min-height: calc(100% - 2rem);
    margin: 1rem auto;
  }

  .matching-guide-modal__body {
    padding: 60px 18px 24px;
    font-size: 14px;
  }

}
</style>
